// ManufacturerForm.tsx
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Loader2, AlertCircle, CheckCircle2, Factory, Mail, Phone, Globe, MapPin } from "lucide-react";
import { Manufacturer } from "../../../types/manufacturer";
import { manufacturerService } from "../../../services/manufacturerService";
import ErrorAlert from "../../../shared/components/ui/ErrorAlert";
import Button from "../../../shared/components/ui/button/Button";

interface ManufacturerFormProps {
  editingItem: Manufacturer | null;
  onAdd: (item: Manufacturer) => void;
  onClose: () => void;
}

export default function ManufacturerForm({
  editingItem,
  onAdd,
  onClose
}: ManufacturerFormProps) {
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty, isValid },
    setFocus
  } = useForm<any>({
    resolver: undefined, // ManufacturerForm doesn't seem to use a schema currently? 
    // Wait, the original code didn't have a resolver?
    defaultValues: {
      name: "",
      code: "",
      description: "",
      contactInfo: {
        email: "",
        phone: "",
        website: "",
        address: ""
      },
      status: "active"
    },
    mode: "onChange"
  });

  const watchedName = watch("name");
  const watchedCode = watch("code");
  const watchedDescription = (watch("description") as string) || "";

  // Set form values when editing
  useEffect(() => {
    if (editingItem) {
      reset({
        name: editingItem.name,
        code: editingItem.code || "",
        description: editingItem.description || "",
        contactInfo: {
          email: editingItem.contactInfo?.email || "",
          phone: editingItem.contactInfo?.phone || "",
          website: editingItem.contactInfo?.website || "",
          address: editingItem.contactInfo?.address || ""
        },
        status: editingItem.status
      });
    } else {
      reset();
    }
    setFocus("name");
  }, [editingItem, reset, setFocus]);

  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    setApiError(null);
    setShowSuccess(false);

    try {
      let res;
      const payload: any = {
        name: data.name.trim(),
        status: "active"
      };

      // Only include optional fields if they have values
      if (data.code?.trim()) {
        payload.code = data.code.trim().toUpperCase();
      }
      if (data.description?.trim()) {
        payload.description = data.description.trim();
      }

      // Contact info - only include if any field has value
      const contactInfo: any = {};
      if (data.contactInfo?.email?.trim()) {
        contactInfo.email = data.contactInfo.email.trim();
      }
      if (data.contactInfo?.phone?.trim()) {
        contactInfo.phone = data.contactInfo.phone.trim();
      }
      if (data.contactInfo?.website?.trim()) {
        contactInfo.website = data.contactInfo.website.trim();
      }
      if (data.contactInfo?.address?.trim()) {
        contactInfo.address = data.contactInfo.address.trim();
      }

      if (Object.keys(contactInfo).length > 0) {
        payload.contactInfo = contactInfo;
      }

      if (editingItem) {
        const manufacturerId = editingItem._id || editingItem.id;
        if (!manufacturerId) {
          throw new Error("Manufacturer ID is required for update");
        }
        res = await manufacturerService.updateManufacturer(String(manufacturerId), payload);
      } else {
        res = await manufacturerService.createManufacturer(payload);
      }

      setShowSuccess(true);
      setTimeout(() => {
        onAdd(res.data);
        onClose();
      }, 1000);
    } catch (err: any) {
      setApiError(err.response?.data?.message || "An error occurred while saving the manufacturer");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {showSuccess && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              {editingItem ? "Manufacturer updated successfully!" : "Manufacturer created successfully!"}
            </p>
          </div>
        </div>
      )}

      {apiError && (
        <ErrorAlert
          message={apiError}
          onClose={() => setApiError(null)}
          className="mb-4"
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Information Section */}
        <div className="bg-gray-50 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Factory className="w-5 h-5" />
            Basic Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name Field */}
            <div className="space-y-2">
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Manufacturer Name *
              </label>
              <div className="relative">
                <input
                  id="name"
                  type="text"
                  {...register("name", {
                    required: "Manufacturer name is required",
                    minLength: {
                      value: 2,
                      message: "Name must be at least 2 characters"
                    },
                    maxLength: {
                      value: 200,
                      message: "Name must be less than 200 characters"
                    },
                  })}
                  className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${errors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedName && !errors.name
                      ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                      : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                    }`}
                  placeholder="Enter manufacturer name"
                />
                {watchedName && !errors.name && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {errors.name && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {errors.name && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.name.message as string}
                </p>
              )}
            </div>

            {/* Code Field */}
            <div className="space-y-2">
              <label
                htmlFor="code"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Manufacturer Code
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <div className="relative">
                <input
                  id="code"
                  type="text"
                  {...register("code", {
                    minLength: {
                      value: 2,
                      message: "Code must be at least 2 characters"
                    },
                    maxLength: {
                      value: 20,
                      message: "Code must be less than 20 characters"
                    },
                    pattern: {
                      value: /^[A-Z0-9-_]+$/i,
                      message: "Code can only contain letters, numbers, hyphens, and underscores"
                    }
                  })}
                  className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 uppercase ${errors.code
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : watchedCode && !errors.code
                      ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                      : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                    }`}
                  placeholder="Enter manufacturer code"
                />
                {watchedCode && !errors.code && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {errors.code && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {errors.code && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.code.message as string}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="bg-gray-50 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Contact Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email */}
            <div className="space-y-2">
              <label
                htmlFor="contactInfo.email"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                <Mail className="w-4 h-4 inline mr-1" />
                Email
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="contactInfo.email"
                type="email"
                {...register("contactInfo.email", {
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Please enter a valid email address"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors.contactInfo as any)?.email
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter email address"
              />
              {(errors.contactInfo as any)?.email && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors.contactInfo as any).email.message as string}
                </p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label
                htmlFor="contactInfo.phone"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                <Phone className="w-4 h-4 inline mr-1" />
                Phone
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="contactInfo.phone"
                type="tel"
                {...register("contactInfo.phone", {
                  pattern: {
                    value: /^[+]?[\d\s\-\(\)]+$/,
                    message: "Please enter a valid phone number"
                  },
                  maxLength: {
                    value: 20,
                    message: "Phone number must be less than 20 characters"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors.contactInfo as any)?.phone
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter phone number"
              />
              {(errors.contactInfo as any)?.phone && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors.contactInfo as any).phone.message as string}
                </p>
              )}
            </div>

            {/* Website */}
            <div className="space-y-2">
              <label
                htmlFor="contactInfo.website"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                <Globe className="w-4 h-4 inline mr-1" />
                Website
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="contactInfo.website"
                type="url"
                {...register("contactInfo.website", {
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: "Please enter a valid URL (starting with http:// or https://)"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${(errors.contactInfo as any)?.website
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="https://example.com"
              />
              {(errors.contactInfo as any)?.website && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors.contactInfo as any).website.message as string}
                </p>
              )}
            </div>

            {/* Address */}
            <div className="space-y-2">
              <label
                htmlFor="contactInfo.address"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                <MapPin className="w-4 h-4 inline mr-1" />
                Address
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <textarea
                id="contactInfo.address"
                rows={3}
                {...register("contactInfo.address", {
                  maxLength: {
                    value: 500,
                    message: "Address must be less than 500 characters"
                  }
                })}
                className={`w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 resize-none ${(errors.contactInfo as any)?.address
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                placeholder="Enter complete address"
              />
              {(errors.contactInfo as any)?.address && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(errors.contactInfo as any).address.message as string}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Description Field */}
        <div className="space-y-2">
          <label
            htmlFor="description"
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Description
            <span className="text-gray-400 font-normal ml-1">(optional)</span>
          </label>
          <div className="relative">
            <textarea
              id="description"
              rows={4}
              {...register("description", {
                maxLength: {
                  value: 1000,
                  message: "Description must be less than 1000 characters"
                }
              })}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 resize-none ${errors.description
                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                : watchedDescription && !errors.description
                  ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                }`}
              placeholder="Enter a brief description of this manufacturer..."
            />
            {watchedDescription && !errors.description && (
              <CheckCircle2 className="absolute right-3 top-4 w-5 h-5 text-green-500" />
            )}
            {errors.description && (
              <AlertCircle className="absolute right-3 top-4 w-5 h-5 text-red-500" />
            )}
          </div>
          <div className="flex items-center justify-between">
            {errors.description && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.description.message as string}
              </p>
            )}
            {watchedDescription && !errors.description && (
              <p className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                Description looks good!
              </p>
            )}
            <span className={`text-xs ${(watchedDescription?.length || 0) > 900 ? 'text-red-500' :
              (watchedDescription?.length || 0) > 800 ? 'text-yellow-500' : 'text-gray-400'
              }`}>
              {watchedDescription?.length || 0}/1000
            </span>
          </div>
        </div>

        {/* Action Buttons - Sticky Footer */}
        <div className="sticky bottom-0 flex flex-col sm:flex-row justify-end gap-3 pt-5 pb-2 border-t border-gray-200 mt-6 bg-white z-10">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="sorigin"
            size="lg"
            disabled={isSubmitting || !isDirty || !isValid}
            className="w-full sm:w-auto min-w-[140px] order-1 sm:order-2"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {editingItem ? "Updating..." : "Creating..."}
              </div>
            ) : editingItem ? (
              "Update Manufacturer"
            ) : (
              "Create Manufacturer"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
